import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ComputedPageItem, SmartGuideConfig} from '@taopix/taopix-core/dist/types/types';
import {Checkbox, GearIcon, ContentPanel} from '@taopix/taopix-design-system';
import {useTaopix} from '../Context/TaopixContext';
import Colour from './BoxSettings/BoxSettingsOptions/Colour';
import {Tile} from '../BackgroundsList/BackgroundsList';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import useSettings from '../../Hooks/useSettings';
import {SettingsActions} from '@taopix/taopix-core';
import ToolPaletteSection from '../ToolPalette/ToolPaletteSection';
import {useExperience} from '../Experience/ExperienceProvider';

export type SettingsPaletteProps = {
  sectionKey: string;
  item?: ComputedPageItem;
};

type guideColour = {
  itemGuideColour: string;
  pageGuideColour: string;
};

const SettingsPalette = ({sectionKey, item}: SettingsPaletteProps) => {
  const app = useTaopix();
  const settings = useSettings();
  const ui = useUiState();
  const {t} = useTranslation(['toolpalette']);
  const {experience} = useExperience();
  const paletteOpen = ui.state.palette.section?.includes(sectionKey) || false;

  let guides: SmartGuideConfig = settings.state.features.smartGuides;
  const smartGuidesEnabled = experience.EDITOR.smartGuides.initialSmartGuideSetting;

  // Update the checkbox with the default experience setting
  useEffect(() => {
    handleChange(smartGuidesEnabled);
  }, [experience.EDITOR.smartGuides]);

  const colours = settings.state.colours.palette;

  if (localStorage.getItem('smartGuides')) {
    guides = JSON.parse(localStorage.getItem('smartGuides') || '');
  }

  const [selectedGuideColour, setSelectedGuideColour] = useState<
    guideColour & {defaultItemGuideColour: string; defaultPageGuideColour: string}
  >({
    itemGuideColour: guides.itemColour,
    defaultItemGuideColour: settings.state.features.smartGuides.itemColour,
    pageGuideColour: guides.pageColour,
    defaultPageGuideColour: settings.state.features.smartGuides.pageColour,
  });

  useEffect(() => {
    onSetGuideColour('itemColour', selectedGuideColour.itemGuideColour);
  }, [selectedGuideColour.itemGuideColour]);

  useEffect(() => {
    onSetGuideColour('pageColour', selectedGuideColour.pageGuideColour);
  }, [selectedGuideColour.pageGuideColour]);

  useEffect(() => {
    // Deselect any items when opening this palette to ensure the context menu doesn't appear over the top of it.
    if (ui.state.palette.section === 'project-settings' && item?.ref !== -1) {
      app.core.dispatch('SHAPE.DESELECT', null).catch(error => {
        console.error(error);
      });
    }
  }, [ui.state.palette.section, paletteOpen]);

  /**
   * Updates the localstorage and Core with the new guides config object.
   *
   * @param guideConfig New guide config.
   */
  const updateGuides = (guideConfig: Partial<SmartGuideConfig>): void => {
    const updatedGuides = {
      ...guides,
      ...guideConfig,
    };

    localStorage.setItem('smartGuides', JSON.stringify(updatedGuides));
    window.Taopix.Core.get('options').guides = updatedGuides;
    settings.dispatch(SettingsActions.updateSmartGuides(updatedGuides));
    guides = updatedGuides;
  };

  /**
   * Updates the Smart Guide Config enabled flag.
   *
   * @param isChecked True if can show smart guides.
   * @param guideConfig Guide config to update.
   */
  const handleChange = (isChecked: boolean): void => {
    const enabled = {
      enabled: isChecked,
    };
    updateGuides(enabled);
  };

  /**
   * Updates the Smart Guide colours.
   *
   * @param guide Which guide colour to update.
   * @param selectedColour The colour to set.
   */
  const onSetGuideColour = (
    guide: 'pageColour' | 'itemColour',
    selectedColour: string | undefined
  ): void => {
    if (selectedColour) {
      const guideColour = {
        [guide]: selectedColour,
      };
      updateGuides(guideColour);
    }
  };

  const sectionButtonClasses = [
    ...(ui.state.isSmallScreen
      ? [
          'threeButtons:w-[calc(100vw/3.5)]',
          'fourButtons:w-[calc(100vw/4.5)]',
          'fiveButtons:w-[calc(100vw/5.5)]',
          'sixButtons:w-[calc(100vw/6.5)]',
          'sevenButtons:w-[calc(100vw/7.5)]',
          'eightButtons:w-[calc(100vw/8.5)]',
        ]
      : ['']),
  ];

  if (!experience.EDITOR.settings.settingsPalette) {
    return null;
  }

  return (
    <ToolPaletteSection
      buttonClasses={sectionButtonClasses.join(' ')}
      sectionKey={sectionKey}
      title={t('*:str_LabelSettings')}
      buttonIcon={<GearIcon />}
      id="project-settings"
    >
      <div
        className={
          'flex flex-col flex-1 overflow-y-auto px-md pb-md space-y-md sm:min-h-mobilePaletteContentHeight'
        }
      >
        <ContentPanel heading={t('*:str_LabelSmartGuides')}>
          <Checkbox
            id="smartGuidesOption"
            className="mb-md"
            label={t('str_LabelShowSmartGuides', {
              ns: 'toolpalette',
            })}
            onChange={isChecked => handleChange(isChecked)}
            defaultChecked={smartGuidesEnabled}
          />
          <div className={'space-y-md'}>
            <Colour
              id={'item-guide-colour-picker'}
              sectionKey={'item-guide-colour'}
              item={item}
              colours={colours}
              ignoreItem={true}
              selectedColour={selectedGuideColour.itemGuideColour}
              parentSection={'project-settings'}
              buttonLabel={t('str_LabelObjectGuideColour', {
                ns: 'toolpalette',
              })}
              sectionTitle={t('str_LabelObjectGuideColour', {
                ns: 'toolpalette',
              })}
              onClick={(
                event: React.MouseEvent | React.ChangeEvent,
                newSelectedBackground: Tile
              ) => {
                if (selectedGuideColour.itemGuideColour === newSelectedBackground?.src) {
                  setSelectedGuideColour(current => {
                    return {
                      ...selectedGuideColour,
                      itemGuideColour: current.defaultItemGuideColour,
                    };
                  });
                } else {
                  setSelectedGuideColour(current => {
                    return {
                      ...selectedGuideColour,
                      itemGuideColour: newSelectedBackground?.src,
                    };
                  });
                }
              }}
            />
            <Colour
              id={'page-guide-colour-picker'}
              sectionKey={'page-guide-colour'}
              item={item}
              colours={colours}
              ignoreItem={true}
              selectedColour={selectedGuideColour.pageGuideColour}
              parentSection={'project-settings'}
              buttonLabel={t('str_LabelPageGuideColour', {
                ns: 'toolpalette',
              })}
              sectionTitle={t('str_LabelPageGuideColour', {
                ns: 'toolpalette',
              })}
              onClick={(
                event: React.MouseEvent | React.ChangeEvent,
                newSelectedBackground: Tile
              ) => {
                if (selectedGuideColour.pageGuideColour === newSelectedBackground?.src) {
                  setSelectedGuideColour(current => {
                    return {
                      ...selectedGuideColour,
                      pageGuideColour: current.defaultPageGuideColour,
                    };
                  });
                } else {
                  setSelectedGuideColour(current => {
                    return {
                      ...selectedGuideColour,
                      pageGuideColour: newSelectedBackground?.src,
                    };
                  });
                }
              }}
            />
          </div>
        </ContentPanel>
      </div>
    </ToolPaletteSection>
  );
};

export default SettingsPalette;
